// graphFocus — "take me to that node".
//
// Two callers, one mechanism: the Ctrl+F search box in the graph editors, and
// the agent's focus_node tool (aos-mcp). Knowing where a node is was never the
// useful part — a bare "(3700, 2600)" is not a place a person can go, and a
// node inside a container (a graph group, a state machine) has its OWN
// coordinate space, so the number alone points nowhere. This module turns a
// located node into an editor that is actually looking at it.
//
// A REQUEST, not a call. Focusing means opening an editor surface and possibly
// drilling into a container, and the canvas that can serve it mounts a frame or
// two LATER — a direct `centerOn(id)` would fire into an unmounted component
// and silently do nothing. So a request is parked here, canvases subscribe, and
// whichever one holds the target serves it and resolves the promise. Nobody
// answers within the timeout → the caller gets `{ ok: false, reason }` and can
// SAY so. That failure is real, not theoretical: the agent reads the blueprint
// SAVED on the server while the user may have unsaved local edits, so "that
// node isn't in the graph you have open" is a state we must report rather than
// swallow into a canvas that just sits there.
//
// Module singleton + subscribe, following graphState / shellLayout /
// liveTuneStore — the canvases read it imperatively and it must never re-render
// React on the hot path.

import menubarCommands from './menubarCommands.js'

/** MCP names the event graph 'event'; the editor surface id is 'events'. */
const SURFACE_BY_GRAPH = Object.freeze({ body: 'body', face: 'face', event: 'events', events: 'events' })

/** Canonical graph key (what the search and the canvases compare on). */
export const GRAPH_KEYS = Object.freeze({ body: 'body', face: 'face', event: 'event', events: 'event' })

export const FOCUS_TIMEOUT_MS = 4000

/** Why a focus request went unserved. Shown to the user, so keep them plain. */
export const FOCUS_REASONS = Object.freeze({
  notFound: 'not-found',
  superseded: 'superseded',
  closed: 'closed',
})

let seq = 0
let pending = null
const listeners = new Set()

function notify() {
  for (const fn of listeners) fn(pending)
}

function settle(result) {
  if (!pending) return
  const { resolve, timer } = pending
  if (timer) clearTimeout(timer)
  pending = null
  resolve(result)
  notify()
}

/**
 * Ask the editor to show a node, or a raw point in a graph's coordinate space.
 *
 * @param {object} req
 * @param {'body'|'face'|'event'|'events'} req.graph
 * @param {string} [req.nodeId]              the node to centre on
 * @param {{x:number,y:number}} [req.point]  …or a bare coordinate (pasted into Ctrl+F)
 * @param {{id:string,name?:string,kind?:string}|null} [req.container]
 *        the graph group / state machine / function holding the node
 * @param {string} [req.label]
 * @returns {Promise<{ok:boolean, reason?:string, container?:object|null}>}
 */
export function requestFocus(req = {}, { timeoutMs = FOCUS_TIMEOUT_MS } = {}) {
  const graphIn = String(req.graph ?? '')
  const surface = SURFACE_BY_GRAPH[graphIn]
  const graph = GRAPH_KEYS[graphIn]
  const nodeId = req.nodeId ? String(req.nodeId) : null
  const point = req.point && Number.isFinite(req.point.x) && Number.isFinite(req.point.y)
    ? { x: Number(req.point.x), y: Number(req.point.y) }
    : null
  if (!surface) return Promise.reject(new Error(`focus: unknown graph "${graphIn}" (expected body|face|event)`))
  if (!nodeId && !point) return Promise.reject(new Error('focus: nodeId or point required'))

  // A newer request wins outright — the user asked for something else.
  if (pending) settle({ ok: false, reason: FOCUS_REASONS.superseded })

  seq += 1
  const mySeq = seq
  const promise = new Promise((resolve) => {
    pending = {
      seq: mySeq,
      graph, surface, nodeId, point,
      container: req.container ?? null,
      label: req.label ?? nodeId ?? `${point?.x}, ${point?.y}`,
      resolve,
      timer: null,
    }
  })
  // Open the surface first: the canvas that can serve this may not be mounted.
  // `editors.open` is registered BY the blueprint editor, so it is absent when
  // no editor is loaded — then nothing can serve the request and it times out
  // honestly rather than pretending to have moved.
  if (menubarCommands.has('editors.open')) menubarCommands.run('editors.open', surface)
  if (pending && timeoutMs > 0) {
    // Not unref'd: the timeout IS the answer when no canvas can serve the
    // request, so it has to actually fire (under node --test an unref'd timer
    // lets the loop drain first and the promise never settles).
    const timer = setTimeout(() => {
      if (pending?.seq === mySeq) settle({ ok: false, reason: FOCUS_REASONS.notFound })
    }, timeoutMs)
    if (pending?.seq === mySeq) pending.timer = timer
  }
  notify()
  return promise
}

/** The unserved request, if any. Canvases read this on mount and on change. */
export function getPendingFocus() { return pending }

/**
 * Serve the pending request, by seq. Called by the canvas that actually centred
 * on the target — never on a near miss, so an unserved request still times out
 * instead of being reported as delivered.
 */
export function completeFocus(reqSeq) {
  if (!pending || pending.seq !== reqSeq) return false
  settle({ ok: true, container: pending.container })
  return true
}

/** Drop the pending request unserved (editor closed, character switched). */
export function cancelFocus(reason = FOCUS_REASONS.closed) {
  if (pending) settle({ ok: false, reason })
}

export function subscribeFocus(fn) {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

/** Test seam — drop state and subscribers. */
export function _resetFocus() {
  if (pending?.timer) clearTimeout(pending.timer)
  pending = null
  listeners.clear()
  seq = 0
}

// Debug/test seam, per the window.__* convention (CLAUDE.md). Console-drivable:
//   __FOCUS_NODE__({ graph: 'event', nodeId: 'key_s_sing' })
//   __FOCUS_NODE__({ graph: 'event', point: { x: 3700, y: 2600 } })
if (typeof window !== 'undefined') {
  window.__FOCUS_NODE__ = requestFocus
  window.__GRAPH_FOCUS__ = { requestFocus, getPendingFocus, completeFocus, cancelFocus, subscribeFocus }
}

export default { requestFocus, getPendingFocus, completeFocus, cancelFocus, subscribeFocus }
